'use client'

import { Image } from 'cms-types'
import React, { useRef } from 'react'

import useInViewCallback from '../../hooks/useInViewCallback'
import { BlogPostCardType, type LoadableComponentProps } from '../../types'
import BlogPostCard from '../BlogPostCard'
import SuggestionCard from '../SuggestionCard'

type BlogGridProps = {
  withHeadingBlock?: boolean
} & LoadableComponentProps<{
  suggestionCard?: {
    title: string
    description: string
    buttonText: string
    image?: Image
    url?: string
  }
  posts: BlogPostCardType[]
  priority: boolean
  onScrollToEnd?: () => void
  isInfiniteScrollLoading?: boolean
  loadingBlogsCount?: number
  onCardClick?: (slug: string) => void
}>

export default function BlogGrid({ withHeadingBlock = true, data, loading }: BlogGridProps) {
  const loaderRef = useRef<HTMLLIElement>(null)

  useInViewCallback(loaderRef, data?.onScrollToEnd)

  return (
    <ul
      data-testid='blog-grid'
      className='mx-auto grid w-full grid-cols-1 gap-x-5 gap-y-12 px-5 md:grid-cols-2 xl:grid-cols-3 xl:gap-y-20 xl:px-10'
    >
      {loading ? (
        <>
          {Array.from({ length: withHeadingBlock ? 4 : 3 }, (_, index) => (
            <React.Fragment key={index}>
              <li
                className={
                  index === 0 && withHeadingBlock
                    ? 'col-span-1 md:col-span-2 xl:col-span-3'
                    : 'col-span-1'
                }
              >
                <BlogPostCard
                  smallImageSizes='(min-width: 768px) 50vw, (min-width: 1280px) 50vw, 100vw'
                  size={index === 0 && withHeadingBlock ? 'big' : 'small'}
                  key={index}
                  loading
                />
              </li>
            </React.Fragment>
          ))}
        </>
      ) : (
        <>
          {data.posts.map((post, index) => {
            const suggestCard = data.suggestionCard && index > 0 && index % 12 === 0 // Show suggestion card every 12th post

            return (
              <React.Fragment key={index}>
                <li
                  className={
                    index === 0 && withHeadingBlock
                      ? 'col-span-1 md:col-span-2 xl:col-span-3'
                      : 'col-span-1'
                  }
                >
                  <BlogPostCard
                    smallImageSizes='(min-width: 768px) 50vw, (min-width: 1280px) 50vw, 100vw'
                    data={{
                      post,
                      priority: data.priority
                        ? withHeadingBlock
                          ? index === 0
                          : index < 3
                        : false,
                      onCardClick: () => data.onCardClick?.(post.slug),
                    }}
                    size={index === 0 && withHeadingBlock ? 'big' : 'small'}
                  />
                </li>

                {suggestCard && data.suggestionCard && (
                  <li className='-mx-5 -my-6 md:col-span-2 xl:col-span-3 xl:m-0'>
                    <SuggestionCard
                      title={data.suggestionCard.title}
                      description={data.suggestionCard.description}
                      buttonText={data.suggestionCard.buttonText}
                      image={data.suggestionCard.image}
                    />
                  </li>
                )}
              </React.Fragment>
            )
          })}

          {data.isInfiniteScrollLoading &&
            Array.from({ length: 3 - ((data.posts.length - 1) % 3) }, (_, index) => (
              <li key={`skeleton-${index}`} className='col-span-1'>
                <BlogPostCard
                  smallImageSizes='(min-width: 768px) 50vw, (min-width: 1280px) 50vw, 100vw'
                  size='small'
                  loading
                />
              </li>
            ))}
          {data.loadingBlogsCount != null &&
            data.loadingBlogsCount !== 0 &&
            Array.from({ length: data.loadingBlogsCount }, (_, index) => (
              <li key={`skeleton-${index}`} className='col-span-1'>
                <BlogPostCard
                  smallImageSizes='(min-width: 768px) 50vw, (min-width: 1280px) 50vw, 100vw'
                  size='small'
                  loading
                />
              </li>
            ))}
        </>
      )}
      <li ref={loaderRef} key='loader-ref' className='block' />
    </ul>
  )
}
